import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Crown } from 'lucide-react';
import { StockCategoryFilter, getCategoryConfigs } from '../../types/stockCategories';
import { getNextCategory, getPreviousCategory } from '../../utils/categoryNavigation';
import { hexToRgba } from '../../utils/color';
import { cn } from '../../utils/cn';
import { SpecialThanksBox } from '../ui/SpecialThanksBox';
import { useCategoryLocking } from '../../hooks/useCategoryLocking/index';

interface CategoryNavigationButtonsProps {
  currentCategory: StockCategoryFilter;
  className?: string;
  showMessage?: boolean;
}

/**
 * CategoryNavigationButtons - Reusable component for navigating between categories
 * Displays Previous and Next category buttons with simplified styling
 */
export const CategoryNavigationButtons: React.FC<CategoryNavigationButtonsProps> = ({
  currentCategory,
  className,
  showMessage = true
}) => {
  const navigate = useNavigate();
  const configs = getCategoryConfigs();
  const { isCategoryLocked, CategoryLockModal } = useCategoryLocking();

  const nextCategory = getNextCategory(currentCategory);
  const previousCategory = getPreviousCategory(currentCategory);

  const nextConfig = configs[nextCategory];
  const previousConfig = configs[previousCategory];
  const nextColor = hexToRgba(nextConfig.color, 1);
  const previousColor = hexToRgba(previousConfig.color, 1);
  const nextBorderColor = hexToRgba(nextConfig.color, 1);
  const previousBorderColor = hexToRgba(previousConfig.color, 1);
  const isNextLocked = isCategoryLocked(nextCategory);
  const isPreviousLocked = isCategoryLocked(previousCategory);

  const handleNextCategory = () => {
    navigate(`/${nextCategory}`);
  };

  const handlePreviousCategory = () => {
    navigate(`/${previousCategory}`);
  };

  return (
    <div className={cn('flex flex-col gap-4 sm:gap-6', className)}>
      <div className="grid grid-cols-2 items-stretch gap-3 sm:gap-4">
        {/* Previous Category Button */}
        <button
          type="button"
          onClick={handlePreviousCategory}
          className={cn(
            'group order-1 md:order-1 flex w-full items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-white',
            isPreviousLocked && 'opacity-70'
          )}
          style={{
            backgroundColor: previousColor,
            borderColor: previousBorderColor
          }}
          aria-label={`Go to ${previousConfig.label} category`}
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <div className="flex flex-col items-start text-left">
            <span className="text-xs">Previous</span>
            <span className="text-sm font-semibold flex items-center gap-1">
              <span>{previousConfig.icon}</span>
              <span className="hidden sm:inline">{previousConfig.label}</span>
              {isPreviousLocked && (
                <Crown className="h-3 w-3 text-yellow-500 dark:text-yellow-400" />
              )}
            </span>
          </div>
        </button>

        {/* Next Category Button */}
        <button
          type="button"
          onClick={handleNextCategory}
          className={cn(
            'group order-2 md:order-3 flex w-full items-center justify-end gap-2 px-4 py-2 rounded-lg border transition-colors text-white',
            isNextLocked && 'opacity-70'
          )}
          style={{
            backgroundColor: nextColor,
            borderColor: nextBorderColor
          }}
          aria-label={`Go to ${nextConfig.label} category`}
        >
          <div className="flex flex-col items-end text-right">
            <span className="text-xs">Next</span>
            <span className="text-sm font-semibold flex items-center gap-1">
              <span className="hidden sm:inline">{nextConfig.label}</span>
              <span>{nextConfig.icon}</span>
              {isNextLocked && (
                <Crown className="h-3 w-3 text-yellow-500 dark:text-yellow-400" />
              )}
            </span>
          </div>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>

      {showMessage && (
        <div>
          <SpecialThanksBox className="mx-auto max-w-3xl" />
        </div>
      )}
      {CategoryLockModal}
    </div>
  );
};

export default CategoryNavigationButtons;
